import { useCallback, useEffect, useMemo, useState } from "react";
import { inr } from "@/lib/format";
import * as reports from "@/lib/reportsApi";

const pad = value => String(value).padStart(2, "0");

const formatIso = day =>
  `${day.getFullYear()}-${pad(day.getMonth() + 1)}-${pad(day.getDate())}`;

const parseDayStart = (str = "06:00") => {
  const parts = String(str || "06:00").trim().split(":");
  const hour = parseInt(parts[0], 10) || 0;
  const minute = parseInt(parts[1], 10) || 0;
  return { hour, minute };
};

// The report day is a BUSINESS day on the restaurant's wall clock.
// If the day-change hour (default 06:00) hasn't passed, the trading session
// belongs to yesterday.
const getBusinessDate = (dayStartStr) => {
  const { hour, minute } = parseDayStart(dayStartStr);
  const now = new Date();
  return new Date(now.getTime() - (hour * 60 + minute) * 60 * 1000);
};

export const isoBusinessDay = (offsetDays, dayStartStr) => {
  const day = getBusinessDate(dayStartStr);
  day.setDate(day.getDate() - offsetDays);
  return formatIso(day);
};

// `bucket` is the restaurant's wall-clock hour with no offset (2026-10-13; it was a
// UTC instant, which put every IST bar at :30). An offset-less ISO string parses as
// local time, so it renders as sent.
const hourLabel = bucket => {
  const at = new Date(bucket);
  return Number.isNaN(at.getTime())
    ? String(bucket).slice(11, 16)
    : `${pad(at.getHours())}:${pad(at.getMinutes())}`;
};

/**
 * Hours in the order a service runs: from the business-day start, so a night's
 * 20:00 … 23:00 comes before 00:00, 01:00 (owner, 2026-10-24 — sorted as text,
 * 01:00 was drawn first).
 */
export const inServiceOrder = (points, dayStartStr) => {
  const { hour: startHour } = parseDayStart(dayStartStr);
  const rank = time => {
    const hour = parseInt(String(time ?? "").slice(0, 2), 10);
    return Number.isNaN(hour) ? 24 : (hour - startHour + 24) % 24;
  };
  return [...points].sort((a, b) => rank(a.time) - rank(b.time) || String(a.time ?? "").localeCompare(String(b.time ?? "")));
};

const RANGE_DAYS = { Today: 0, Week: 6, Month: 29 };

const sum = (rows, key) => rows.reduce((total, row) => total + Number(row[key] || 0), 0);

// The headline figures use the one shared formatter (configured symbol, paise),
// so they read like the panels beside them — they printed ₹57,810 next to
// Rs.40,582.00 (2026-10-13).

export function useReports(token, period = "Today", options = {}) {
  const [eod, setEod] = useState(null);
  const [taxes, setTaxes] = useState(null);
  const [sales, setSales] = useState([]);
  const [hourly, setHourly] = useState([]);
  const [categories, setCategories] = useState([]);
  const [payments, setPayments] = useState([]);
  const [salesReport, setSalesReport] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rangeIncomplete, setRangeIncomplete] = useState(false);
  // The dates the figures on screen were fetched for, so the page can name them.
  const [range, setRange] = useState(null);

  const dayStart = options?.dayStart || "06:00";
  const customFrom = options?.from;
  const customTo = options?.to;

  const isCustom = period === "Custom";
  const days = RANGE_DAYS[period] ?? 0;

  const load = useCallback(async () => {
    if (!token) return;

    if (isCustom) {
      if (!customFrom || !customTo) {
        setRangeIncomplete(true);
        setLoading(false);
        return;
      }
    }
    setRangeIncomplete(false);
    setLoading(true);

    try {
      const todayIso = isoBusinessDay(0, dayStart);
      const range = isCustom
        ? { fromDate: customFrom, toDate: customTo }
        : { fromDate: isoBusinessDay(days, dayStart), toDate: todayIso };

      const [eodData, taxData, salesData, hourlyData, categoryData, paymentsData, salesReportData] = await Promise.all([
        reports.getEod(todayIso, token),
        reports.getTaxes(range, token),
        reports.getSales({ ...range, groupBy: "day" }, token),
        reports.getSales({ ...range, groupBy: "hour" }, token),
        reports.getSales({ ...range, groupBy: "category" }, token),
        reports.getPayments
          ? Promise.resolve(reports.getPayments(range, token)).catch(() => [])
          : Promise.resolve([]),
        // Feeds one tile and the donut: its failure is shown there, by name, and
        // must not blank the whole overview.
        reports.getSalesReport
          ? Promise.resolve(reports.getSalesReport(range, token)).catch(() => ({ failed: true }))
          : Promise.resolve(null),
      ]);

      setEod(eodData);
      setTaxes(taxData);
      setSales(salesData || []);
      setHourly(hourlyData || []);
      setCategories(categoryData || []);
      setPayments(Array.isArray(paymentsData) ? paymentsData : []);
      setSalesReport(salesReportData || null);
      setRange(range);
      setError(null);
    } catch (failure) {
      setError(failure?.response?.data?.detail || "Could not load reports");
    } finally {
      setLoading(false);
    }
  }, [token, period, isCustom, customFrom, customTo, dayStart, days]);

  useEffect(() => {
    load();
  }, [load]);

  const stats = useMemo(() => {
    if (!eod) return [];
    const multiDay = isCustom ? customFrom !== customTo : days > 0;
    // EOD is fetched for today only; any other range (a single past custom day
    // included) is summed from the range's own buckets.
    const isToday = !isCustom && days === 0;
    const revenue = isToday ? Number(eod.revenue || 0) : sum(sales, "revenue");
    const orders = isToday ? eod.order_count : sum(sales, "order_count");
    const tax = Number(taxes?.totals?.tax_total ?? eod.tax ?? 0);
    return [
      { label: "Gross sales", value: inr(revenue) },
      { label: "Net of tax", value: inr(revenue - tax) },
      { label: "Total orders", value: String(orders) },
      { label: "Average bill", value: inr(orders ? revenue / orders : 0) },
      // The period's cancelled orders, from the Sales report. "Voided today" sat
      // among period figures and read 0 for any range that did not include today.
      {
        label: "Cancelled orders",
        value: salesReport?.failed
          ? "Could not load"
          : String(salesReport?.totals?.cancelled_orders ?? (multiDay ? 0 : eod.void_count ?? 0)),
      },
    ];
  }, [days, isCustom, customFrom, customTo, eod, sales, taxes, salesReport]);

  const hourlySeries = useMemo(() => {
    const hourMap = new Map();
    for (const row of hourly) {
      if (!row.bucket) continue;
      const time = hourLabel(row.bucket);
      const rev = Number(row.revenue || 0);
      hourMap.set(time, (hourMap.get(time) || 0) + rev);
    }
    return inServiceOrder(
      Array.from(hourMap.entries()).map(([time, sales]) => ({ time, sales })),
      dayStart
    );
  }, [hourly, dayStart]);

  const paymentSeries = useMemo(() => {
    // The tenders themselves (order_payments), from the Sales report. /payments
    // groups by the bill's payment_method text, so a split bill was one slice
    // labelled "cash + upi" carrying the whole bill (2026-10-13).
    const modes = salesReport?.payment_modes;
    if (Array.isArray(modes) && modes.length > 0 && salesReport?.totals) {
      const series = modes
        .map(key => ({ name: key.replace(/^paid_/, "").replaceAll("_", " "), value: Number(salesReport.totals[key] || 0) }))
        .filter(entry => entry.value > 0);
      if (series.length) return series;
    }
    if (payments && payments.length > 0) {
      return payments.map(entry => ({
        name: entry.payment_method || entry.mode || "Unknown",
        value: Number(entry.revenue ?? entry.amount ?? 0),
      }));
    }
    if (Array.isArray(eod?.payment_split) && eod.payment_split.length > 0) {
      return eod.payment_split.map(entry => ({
        name: entry?.mode || "Unknown",
        value: Number(entry?.amount || 0),
      }));
    }
    return [];
  }, [payments, eod, salesReport]);

  const topCategories = useMemo(
    () =>
      categories
        .slice()
        .sort((a, b) => Number(b.revenue) - Number(a.revenue))
        .slice(0, 5)
        .map(row => ({
          name: row.bucket,
          bucket: row.bucket,
          qty: Number(row.qty || 0),
          revenue: Number(row.revenue || 0),
          revenue_with_tax: Number(row.revenue_with_tax ?? row.revenue ?? 0),
          amountWithTax: Number(row.revenue_with_tax ?? row.revenue ?? 0),
        })),
    [categories]
  );

  return {
    eod,
    stats,
    hourlySeries,
    paymentSeries,
    topCategories,
    error,
    loading,
    rangeIncomplete,
    range,
    reload: load,
  };
}
